import { produce } from 'immer';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  setValidationErrors,
  showErrorToast,
} from '../../../../../../../common/global/helpers';
import { useDirtyState } from '../../../../../../../common/hooks/useDirtyState';
import Button from '../../../../../../../common/widgets/Button';
import DatePicker from '../../../../../../../common/widgets/DatePicker';
import Dropdown from '../../../../../../../common/widgets/Dropdown';
import { ErrorPanel } from '../../../../../../../common/widgets/ErrorPanel';
import Input from '../../../../../../../common/widgets/Input';
import Loader from '../../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../../common/widgets/LoadingSpinner';
import { toUtcStartOfDay } from '../../../../../helpers';
import {
  CustomFieldModel,
  UpdateEmployeeData,
} from '../../../../../models/EmployeeDetailsModel';
import { ErrorState, ErrorType } from '../../../../../models/Errors';
import { resources } from '../../../../../resources/resources';
import { employeeAdminService } from '../../../../../services/employee-admin';
import NotFound from '../../../../ProductsPage/components/NotFound';
import { EditEmployeeState } from '../EditEmployeeState';
import { mapCustomField } from '../helper';

const toComparableDate = (value: Date | string | undefined | null) => {
  if (!value) return null;

  if (typeof value === 'string') {
    return value.length >= 10 ? value.slice(0, 10) : value;
  }

  if (Number.isNaN(value.getTime())) {
    return null;
  }

  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

interface EditEmployeeProps {
  employeeId: number;
}

export default function EditEmployee(props: Readonly<EditEmployeeProps>) {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const { employeeId } = props;

  const [state, setState] = useState<EditEmployeeState>(
    {} as EditEmployeeState,
  );
  const [errors, setErrors] = useState<ErrorState>({} as ErrorState);

  const selectedGroup = useMemo(
    () => state.groups?.find(group => group.id === state.employee?.groupId),
    [state.groups, state.employee?.groupId],
  );

  const selectedAddress = useMemo(
    () =>
      state.addresses?.find(
        address => address.addressId === state.employee?.addressId,
      ),
    [state.addresses, state.employee?.addressId],
  );

  const selectedState = useMemo(
    () =>
      state.employee
        ? state.states?.find(
            item => item.id === (state.employee?.status ? 1 : 2),
          )
        : undefined,
    [state.states, state.employee],
  );

  const formData = useMemo<UpdateEmployeeData | null>(() => {
    if (!state.employee?.groupId) {
      return null;
    }

    return {
      employeeId,
      customerGroupId: state.employee.groupId,
      eid: state.employee.eid,
      firstName: state.employee.firstName,
      lastName: state.employee.lastName,
      email: state.employee.email,
      phoneNumber: state.employee.phone,
      autoBudget: state.employee.autoBudget,
      cnp: state.employee.cnp,
      costCenter: state.employee.costCenter,
      department: state.employee.department,
      employmentDate: state.employee.employmentDate
        ? toUtcStartOfDay(state.employee.employmentDate)
        : undefined,
      liquidationDate: state.employee.liquidationDate
        ? toUtcStartOfDay(state.employee.liquidationDate)
        : null,
      status: state.employee.status,
      addressId: state.employee.addressId,
      customFieldJson: JSON.stringify(state.customFieldsValues ?? {}),
    };
  }, [employeeId, state.employee, state.customFieldsValues]);

  const dirtyFormData = useMemo(
    () =>
      formData
        ? {
            ...formData,
            employmentDate: toComparableDate(formData.employmentDate),
            liquidationDate: toComparableDate(formData.liquidationDate),
          }
        : null,
    [formData],
  );

  const isFormReady =
    !!formData &&
    !!state.groups &&
    !!state.addresses &&
    !!state.states &&
    (!state.employee?.addressId || !!selectedAddress?.addressId);

  const { isDirty } = useDirtyState(dirtyFormData, isFormReady, employeeId);

  const loadGroups = async (abortController?: AbortController) => {
    const response = await employeeAdminService.getGroups({
      signal: abortController?.signal,
    });
    if (!response.hasErrors) {
      setState(
        produce(d => {
          d.groups = response.data;
        }),
      );
    } else {
      setState(
        produce(d => {
          d.errorType = response.errorType;
        }),
      );
    }
  };

  const loadAddresses = async (abortController?: AbortController) => {
    const response = await employeeAdminService.getAddresses({
      signal: abortController?.signal,
    });
    if (!response.hasErrors) {
      setState(
        produce(d => {
          d.addresses = response.data;
        }),
      );
    } else {
      setState(
        produce(d => {
          d.errorType = response.errorType;
        }),
      );
    }
  };

  const loadEmployeeDetails = useCallback(
    async (abortController?: AbortController) => {
      const response = await employeeAdminService.getEmployeeDetails(
        employeeId,
        {
          signal: abortController?.signal,
        },
      );
      if (!response.hasErrors) {
        const customFieldValuesParsed = JSON.parse(
          response.data.customFieldJson,
        );
        const customFieldValues: { [key: number]: string } =
          customFieldValuesParsed &&
          typeof customFieldValuesParsed === 'object' &&
          !Array.isArray(customFieldValuesParsed)
            ? customFieldValuesParsed
            : {};

        setState(
          produce(d => {
            d.employee = response.data;
            d.customFieldsValues = customFieldValues;
          }),
        );
      } else {
        setState(
          produce(d => {
            d.errorType = response.errorType;
          }),
        );
      }
    },
    [employeeId],
  );

  const handleSave = async () => {
    if (!formData) return;

    setState(
      produce(d => {
        d.isSaving = true;
      }),
    );

    const response = await employeeAdminService.updateEmployee(formData);
    if (!response.hasErrors) {
      navigate('/admin/employee-management');
    } else if (response.errorType === ErrorType.ValidationError) {
      setValidationErrors(response, setErrors);
    } else {
      showErrorToast(
        translate(
          resources.admin.employees.details.general.errorUpdateEmployee,
        ),
      );
    }
    setState(
      produce(d => {
        d.isSaving = false;
      }),
    );
  };

  const handleCancel = () => {
    navigate('/admin/employee-management');
  };

  const clearErrors = (name: string) => {
    setErrors(
      produce(draft => {
        if (draft) {
          draft[name] = [];
        }
      }),
    );
  };

  useEffect(
    (abortController?: AbortController) => {
      const states = [
        { id: 1, name: 'Active' },
        { id: 2, name: 'Inactive' },
      ];
      setState(
        produce(d => {
          d.states = states;
        }),
      );
      loadEmployeeDetails(abortController);
      loadGroups(abortController);
      loadAddresses(abortController);
    },
    [loadEmployeeDetails],
  );

  if (state.errorType === ErrorType.NotFoundError) return <NotFound />;
  if (state.errorType === ErrorType.InternalServerError) return <ErrorPanel />;

  return (
    <Loader
      loading={!state.employee || !state.addresses || !state.groups}
      loader={<LoadingSpinner />}
    >
      <div className="admin-form">
        <div className="row">
          <div className="col col-6 col-xxs-12">
            <Dropdown
              data={state.groups}
              dataItemKey="id"
              textField="name"
              label={translate(resources.admin.employees.details.general.group)}
              value={selectedGroup}
              onChange={e =>
                setState(
                  produce(d => {
                    if (d.employee) d.employee.groupId = e.value.id;
                  }),
                )
              }
              errors={errors.customerGroupId}
              clearErrors={() => clearErrors('customerGroupId')}
              controlClassName="admin-dropdown"
              popupClassName="admin-dropdown-list"
            />
          </div>
          <div className="col col-6 col-xxs-12">
            <Input
              value={state.employee?.firstName ?? ''}
              label={translate(
                resources.admin.employees.details.general.firstName,
              )}
              onChange={e =>
                setState(
                  produce(d => {
                    if (d.employee) d.employee.firstName = e.value.toString();
                  }),
                )
              }
              mandatory
              errors={errors.firstName}
              clearErrors={() => clearErrors('firstName')}
            />
          </div>

          <div className="col col-6 col-xxs-12">
            <Input
              value={state.employee?.lastName ?? ''}
              label={translate(
                resources.admin.employees.details.general.lastName,
              )}
              onChange={e =>
                setState(
                  produce(d => {
                    if (d.employee) d.employee.lastName = e.value.toString();
                  }),
                )
              }
              mandatory
              errors={errors.lastName}
              clearErrors={() => clearErrors('lastName')}
            />
          </div>
          <div className="col col-6 col-xxs-12">
            <Input
              value={state.employee?.email ?? ''}
              label={translate(resources.admin.employees.details.general.email)}
              onChange={e =>
                setState(
                  produce(d => {
                    if (d.employee) d.employee.email = e.value.toString();
                  }),
                )
              }
              mandatory
              errors={errors.email}
              clearErrors={() => clearErrors('email')}
            />
          </div>

          <div className="col col-6 col-xxs-12">
            <Input
              value={state.employee?.phone ?? ''}
              label={translate(
                resources.admin.employees.details.general.phoneNumber,
              )}
              onChange={e =>
                setState(
                  produce(d => {
                    if (d.employee) d.employee.phone = e.value.toString();
                  }),
                )
              }
              errors={errors.phoneNumber}
              clearErrors={() => clearErrors('phoneNumber')}
            />
          </div>

          <div className="col col-6 col-xxs-12">
            {selectedGroup?.showCnpField && (
              <Input
                value={state.employee?.cnp ?? ''}
                label={translate(resources.admin.employees.details.general.cnp)}
                onChange={e =>
                  setState(
                    produce(d => {
                      if (d.employee) d.employee.cnp = e.value.toString();
                    }),
                  )
                }
                mandatory
                errors={errors.cnp}
                clearErrors={() => clearErrors('cnp')}
              />
            )}
          </div>

          {!!selectedGroup?.customFields &&
            selectedGroup.customFields.map((cf: CustomFieldModel) =>
              mapCustomField(cf, state, setState, errors),
            )}

          <div className="col col-6 col-xxs-12">
            <Input
              value={state.employee?.eid}
              label={translate(resources.admin.employees.details.general.eid)}
              onChange={e =>
                setState(
                  produce(d => {
                    if (d.employee) d.employee.eid = e.value;
                  }),
                )
              }
              mandatory={true}
              errors={errors?.eid}
              clearErrors={() => clearErrors('eid')}
            />
          </div>

          <div className="col col-6 col-xxs-12">
            <Dropdown
              data={state.addresses}
              dataItemKey="itemKey"
              textField="address"
              label={translate(
                resources.admin.employees.details.general.address,
              )}
              value={selectedAddress}
              onChange={e => {
                setState(
                  produce(d => {
                    if (d.employee) d.employee.addressId = e.value.addressId;
                  }),
                );
              }}
              mandatory
              errors={errors.addressId}
              clearErrors={() => clearErrors('addressId')}
              controlClassName="admin-dropdown"
              popupClassName="admin-dropdown-list"
            />
          </div>

          <div className="col col-6 col-xxs-12">
            <DatePicker
              value={
                state.employee?.employmentDate
                  ? new Date(state.employee.employmentDate)
                  : undefined
              }
              label={translate(
                resources.admin.employees.details.general.employmentDate,
              )}
              onChange={value =>
                setState(
                  produce(d => {
                    if (d.employee) d.employee.employmentDate = value;
                  }),
                )
              }
              mandatory
              showClearButton
              errors={errors.employmentDate}
              clearErrors={() => clearErrors('employmentDate')}
            />
          </div>

          <div className="col col-6 col-xxs-12">
            <DatePicker
              value={
                state.employee?.liquidationDate
                  ? new Date(state.employee.liquidationDate)
                  : undefined
              }
              label={translate(
                resources.admin.employees.details.general.liquidationDate,
              )}
              onChange={value =>
                setState(
                  produce(d => {
                    if (d.employee) d.employee.liquidationDate = value ?? null;
                  }),
                )
              }
              showClearButton
              errors={errors.liquidationDate}
              clearErrors={() => clearErrors('liquidationDate')}
            />
          </div>

          <div className="col col-6 col-xxs-12">
            <Input
              value={state.employee?.costCenter}
              label={translate(
                resources.admin.employees.details.general.costCenter,
              )}
              onChange={e =>
                setState(
                  produce(d => {
                    if (d.employee) d.employee.costCenter = e.value.toString();
                  }),
                )
              }
              errors={errors?.costCenter}
              clearErrors={() => clearErrors('costCenter')}
            />
          </div>

          <div className="col col-6 col-xxs-12">
            <Input
              value={state.employee?.department ?? ''}
              label={translate(
                resources.admin.employees.details.general.department,
              )}
              onChange={e =>
                setState(
                  produce(d => {
                    if (d.employee) d.employee.department = e.value.toString();
                  }),
                )
              }
            />
          </div>

          <div className="col col-6 col-xxs-12">
            <Dropdown
              data={state.states}
              dataItemKey="id"
              textField="name"
              label={translate(resources.admin.employees.add.state)}
              value={selectedState}
              onChange={e =>
                setState(
                  produce(d => {
                    if (d.employee) d.employee.status = e.value.id === 1;
                  }),
                )
              }
              mandatory={true}
              errors={errors?.status}
              clearErrors={() => clearErrors('status')}
              controlClassName="admin-dropdown"
              popupClassName="admin-dropdown-list"
            />
          </div>

          <div className="col col-6 col-xxs-12">
            <Input
              type="number"
              value={state.employee?.autoBudget}
              label={translate(
                resources.admin.employees.details.general.autoBudget,
              )}
              onChange={e =>
                setState(
                  produce(d => {
                    if (d.employee) d.employee.autoBudget = Number(e.value);
                  }),
                )
              }
              min={0}
              errors={errors?.autoBudget}
              clearErrors={() => clearErrors('autoBudget')}
            />
          </div>
        </div>
        <div className="form-actions">
          <Button
            className="btn btn-solid"
            onClick={handleSave}
            loading={state.isSaving}
            disabled={!isDirty}
          >
            <span className="btn-text">
              {translate(resources.admin.employees.details.general.save)}
            </span>
          </Button>
          <Button className="btn btn-outline" onClick={handleCancel}>
            <span className="btn-text">
              {translate(resources.admin.employees.details.general.cancel)}
            </span>
          </Button>
        </div>
      </div>
    </Loader>
  );
}
